/**
 * EmailDeliverabilityCard — admin settings report card (Iter 150).
 *
 * Shows the verified sender, 30-day send/delivered/bounced/failed stats,
 * recent problem emails (so bad patient addresses get spotted early), and
 * one-time webhook setup instructions for delivery/bounce tracking.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';

function Tile({ label, value, tone }) {
  const tones = {
    ok: 'text-[#2E7D4F]',
    bad: 'text-[#B83A3A]',
    muted: 'text-fg',
  };
  return (
    <div className="rounded-lg border border-soft px-3 py-2.5 text-center">
      <div className={`text-2xl font-heading font-bold ${tones[tone] || tones.muted}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-fg-muted mt-0.5">{label}</div>
    </div>
  );
}

export default function EmailDeliverabilityCard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get('/email/deliverability').then(r => setD(r.data)).catch(()=>{});
  }, []);

  const webhookUrl = `${window.location.origin}/api/webhooks/resend`;

  return (
    <details className="admin-card md:col-span-2" data-cat="operations" data-testid="email-deliverability-card">
      <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-brand"/>
          <span className="font-heading font-semibold">Email deliverability</span>
          {d?.custom_domain && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#2E7D4F]/10 text-[#2E7D4F]" data-testid="deliverability-verified-chip">
              <CheckCircle2 className="w-3 h-3"/> {d.sender} · verified
            </span>
          )}
        </div>
        {d && d.bounce_rate_pct > 0 && (
          <span className="text-xs text-[#B83A3A]">{d.bounce_rate_pct}% bounce rate</span>
        )}
      </summary>

      {!d ? (
        <div className="text-xs text-fg-muted mt-4">Loading…</div>
      ) : (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-fg-muted">
            All clinic emails (reminders, invoices, pay stubs, GP letters) send from{' '}
            <strong className="text-fg">{d.sender}</strong>
            {d.custom_domain ? ' — your own verified domain, so they land in inboxes, not spam.' : ' — the Resend sandbox sender.'}
            {' '}Last {d.window_days} days:
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="deliverability-tiles">
            <Tile label="Sent" value={d.total_sent} tone="muted"/>
            <Tile label="Delivered" value={d.counts.DELIVERED} tone="ok"/>
            <Tile label="Bounced" value={d.counts.BOUNCED + d.counts.COMPLAINED} tone={d.counts.BOUNCED + d.counts.COMPLAINED > 0 ? 'bad' : 'muted'}/>
            <Tile label="Failed to send" value={d.counts.FAILED} tone={d.counts.FAILED > 0 ? 'bad' : 'muted'}/>
          </div>

          {!d.webhook_active && (
            <div className="rounded-lg border border-[#C89B3C]/40 bg-[#FFF8E7] px-3 py-2.5 text-xs text-[#7A5500]" data-testid="deliverability-webhook-hint">
              <div className="font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5"/> Turn on delivery & bounce tracking (one-time, 2 minutes)</div>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Open <strong>resend.com → Webhooks → Add endpoint</strong></li>
                <li>Endpoint URL: <code className="bg-white/70 px-1 rounded select-all">{webhookUrl}</code></li>
                <li>Select events: <strong>email.delivered, email.bounced, email.complained, email.delivery_delayed</strong></li>
              </ol>
              <div className="mt-1">Until then, "Delivered / Bounced" stay at 0 — sends are still tracked.</div>
            </div>
          )}

          {d.recent_problems.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-fg-muted mb-1.5">Needs attention — check these addresses</h4>
              <table className="w-full text-xs" data-testid="deliverability-problems-table">
                <tbody>
                  {d.recent_problems.map((r, i) => (
                    <tr key={i} className="border-b border-soft/50 last:border-0">
                      <td className="py-1.5 pr-2 font-medium text-fg">{r.to}</td>
                      <td className="py-1.5 pr-2 text-fg-muted truncate max-w-[220px]">{r.subject}</td>
                      <td className="py-1.5 pr-2"><span className="text-[#B83A3A] font-medium">{r.delivery_status}</span>{r.bounce_reason ? ` · ${r.bounce_reason}` : ''}</td>
                      <td className="py-1.5 text-fg-muted whitespace-nowrap">{(r.sent_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-xs">
            <Link to="/admin/system-status" className="underline text-brand hover:opacity-80" data-testid="deliverability-full-log-link">
              Full email log & one-click resend →
            </Link>
          </div>
        </div>
      )}
    </details>
  );
}
